import { isEmpty, isEqual, orderBy } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

import { Chip, Grid, Tooltip, Typography, FormControl, List, MenuItem, Select } from '@mui/material'
import { getAllColumnsByView } from '../../../../data/datagrid/CreatViewComponents/ViewDialogUtils'
import { haveSameType } from '../../utils/functions'

export function PrimaryFilterKey(props) {
	const {
		viewData,
		dashboardKeyList,
		setDashboardKeyList,
		columnsByView,
		setColumnsByView,
		primaryFilters,
		onFilterDataChange,
	} = props
	const { t } = useTranslation('common')
	const { environment } = useSelector((state) => state)
	const secondQueryCondition = useMemo(
		() => viewData?.queries?.[0]?.filter?.conditions?.[1],
		[viewData]
	)

	function onFieldChange(key, newDashboardKeyId) {
		if (onFilterDataChange) {
			onFilterDataChange('primaryFilters', {
				...primaryFilters,
				[key]: newDashboardKeyId,
			})
		}
	}

	const {
		data: allColumns,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ['datagridSetting', 'getAllColumnsByView', viewData?.type],
		queryFn: () =>
			getAllColumnsByView({
				viewDataType: viewData?.type,
				apiToken: environment?.apiToken,
				formKey: viewData?.formKey,
			}),
		enabled: !!viewData?.type,
	})

	useEffect(() => {
		if (isLoading || isFetching || isEmpty(allColumns) || isEmpty(dashboardKeyList)) return

		const newDashboardKeyList = dashboardKeyList.map((dashboardKey) => {
			const column = allColumns?.find((column) => column.name === dashboardKey.key)
			if (!column) return dashboardKey

			return {
				...dashboardKey,
				columnType: column.type,
			}
		})

		if (isEqual(newDashboardKeyList, dashboardKeyList)) return
		setDashboardKeyList?.(newDashboardKeyList)
	}, [isLoading, isFetching, dashboardKeyList, allColumns])

	useEffect(() => {
		if (isLoading || isFetching || isEmpty(allColumns)) return

		const key = viewData?.key
		const currentColumns = columnsByView?.[key]
		const orderedColumns = orderBy(allColumns, 'name')
		if (isEmpty(currentColumns)) {
			setColumnsByView?.((prev) => ({
				...prev,
				[key]: orderedColumns,
			}))
			return
		} else {
			if (isEqual(currentColumns, orderedColumns)) return
			setColumnsByView?.((prev) => ({
				...prev,
				[key]: orderedColumns,
			}))
		}
	}, [isLoading, isFetching, allColumns, columnsByView])

	return (
		<>
			<Typography variant="subtitle2" sx={{ pl: 1 }}>
				Primary Filter Key
			</Typography>
			<List
				sx={{
					width: '100%',
					minHeight: 180,
					maxHeight: 180,
					overflowY: 'scroll',
					border: '1px solid #ddd',
					borderRadius: 1,
					mb: 2,
				}}
				dense
			>
				<Grid
					container
					spacing={2}
					sx={{
						px: 2,
					}}
				>
					<Grid item xs={4}>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							{t('common:dis.keyName')}
						</Typography>
					</Grid>
					<Grid item xs={4}>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							{t('common:dis.tileName')}
						</Typography>
					</Grid>
					<Grid item xs={4}>
						<Typography variant="subtitle2"> {t('common:dis.fields')}</Typography>
					</Grid>
				</Grid>
				{!isEmpty(secondQueryCondition) && (
					<PrimaryFilterConditionItem
						queryCondition={secondQueryCondition}
						columns={allColumns}
						dashboardKeyList={dashboardKeyList}
						conditionLinkedDashboardKey={primaryFilters?.secondConditionLinkedDashboardKey}
						onFieldChange={onFieldChange}
						primaryConditionLinkedDashboardSettingKey="secondConditionLinkedDashboardKey"
					/>
				)}
			</List>
		</>
	)
}

const PrimaryFilterConditionItem = ({
	queryCondition,
	columns,
	dashboardKeyList,
	conditionLinkedDashboardKey,
	onFieldChange,
	primaryConditionLinkedDashboardSettingKey,
}) => {
	const [tileName, setTileName] = useState('')

	const secondConditionColumn = useMemo(() => {
		return columns?.find((column) => column.name === queryCondition?.target)
	}, [queryCondition, columns])

	const linkedDashboardOptions = useMemo(() => {
		if (isEmpty(dashboardKeyList)) {
			return []
		}

		return dashboardKeyList.filter((dashboardKey) => {
			if (!Object.prototype.hasOwnProperty.call(dashboardKey, 'columnType')) return true
			return haveSameType(
				dashboardKey.columnType?.toLowerCase(),
				secondConditionColumn?.type?.toLowerCase()
			)
		})
	}, [secondConditionColumn, dashboardKeyList])

	useEffect(() => {
		const selectedField = linkedDashboardOptions?.find(
			(field) => field?.id === conditionLinkedDashboardKey
		)
		if (!selectedField) {
			if (isEqual(tileName, '')) return
			setTileName('')
			return
		}

		if (isEqual(tileName, selectedField?.tileName)) return
		setTileName(selectedField?.tileName)
	}, [linkedDashboardOptions, conditionLinkedDashboardKey])

	function handleFieldChange(newDashboardKeyId) {
		if (isEmpty(newDashboardKeyId)) {
			setTileName('')
			if (onFieldChange) {
				onFieldChange(primaryConditionLinkedDashboardSettingKey, '')
			}
		}
		const selectedField = linkedDashboardOptions?.find((field) => field?.id === newDashboardKeyId)
		if (!selectedField) return

		if (!isEqual(tileName, selectedField?.tileName)) {
			setTileName(selectedField?.tileName)
		}

		if (onFieldChange) {
			onFieldChange(primaryConditionLinkedDashboardSettingKey, newDashboardKeyId)
		}
	}

	return (
		<Grid
			container
			spacing={2}
			sx={{
				px: 2,
			}}
		>
			<Grid item xs={4}>
				<Tooltip placement="left" title={secondConditionColumn?.title || ''}>
					<Typography
						sx={{
							mb: 2,
							width: '100%',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							display: 'block',
							WebkitLineClamp: '1',
							WebkitBoxOrient: 'vertical',
						}}
					>
						{secondConditionColumn?.title}
					</Typography>
				</Tooltip>
			</Grid>

			{!isEmpty(linkedDashboardOptions) && (
				<>
					<Grid item xs={4}>
						<Tooltip placement="left" title={tileName || ''}>
							<Typography
								sx={{
									mb: 2,
									width: '100%',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									display: 'block',
									WebkitLineClamp: '1',
									WebkitBoxOrient: 'vertical',
								}}
							>
								{tileName}
							</Typography>
						</Tooltip>
					</Grid>
					<Grid item xs={4}>
						{linkedDashboardOptions?.length === 0 && <Chip label="Empty field" />}
						{linkedDashboardOptions?.length > 0 && (
							<FormControl
								variant="standard"
								size="small"
								fullWidth
								sx={{
									'& .MuiInputLabel-root': {
										transform: 'translate(0, 3px) scale(1) !important',
									},
									'& .MuiInput-root': {
										mt: '0 !important',
									},
								}}
							>
								<Select
									labelId="field-select-small-label"
									id="field-select-small"
									value={conditionLinkedDashboardKey || ''}
									onChange={(e) => {
										// onFieldsChange?.(linkedDashboardOptions?.id, e.target.value)
										handleFieldChange(e.target.value)
									}}
								>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{linkedDashboardOptions?.length > 0 &&
										linkedDashboardOptions.map((field, index) => (
											<MenuItem value={field?.id} key={field?.id || index}>
												{field?.key}
											</MenuItem>
										))}
								</Select>
							</FormControl>
						)}
					</Grid>
				</>
			)}
		</Grid>
	)
}

export default PrimaryFilterKey
